import { t as tr } from './i18n';
import { mailDate } from './mail';

/**
 * Verificarea furnizorului la ANAF (serviciul public PlatitorTvaRest v9: fără
 * cont, fără certificat). Din CUI aflăm numele, adresa și Nr. Reg. Com. și,
 * mai ales, dacă firma e **inactivă fiscal**: facturile unui furnizor inactiv
 * nu dau drept de deducere a TVA-ului și a cheltuielii, deci cumpărătorul
 * plătește factura, dar fiscal e ca și cum n-ar avea-o.
 *
 * Datele sunt ale ANAF, la data verificării; de aceea le arătăm mereu cu data.
 */

export interface AnafInfo {
  /** ISO 8601: când am întrebat ANAF */
  checkedAt: string;
  /** false = CUI-ul nu există în registrul ANAF */
  found: boolean;
  name: string;
  /** sediul social, scris ca pe o comandă */
  address: string;
  regCom: string;
  caen: string;
  /** plătitor de TVA */
  vat: boolean;
  /** TVA la încasare: deduci TVA-ul abia după ce plătești factura */
  vatOnCollection: boolean;
  splitVat: boolean;
  inactive: boolean;
  /** 'AAAA-LL-ZZ' */
  inactiveSince: string | null;
  /** 'AAAA-LL-ZZ': firma a fost radiată */
  deregisteredAt: string | null;
  eFactura: boolean;
}

/** Un avertisment despre furnizor: `danger` oprește la alegere și la comandă, `info` doar spune. */
export interface AnafAlert {
  level: 'danger' | 'info';
  text: string;
}

/** CUI-ul ca număr, cum îl vrea ANAF: fără „RO”, fără spații; null = nu e un CUI. */
export function anafCui(raw: string): number | null {
  const m = /^(?:RO)?(\d{2,10})$/i.exec(raw.replace(/[\s.-]/g, ''));
  return m ? Number(m[1]) : null;
}

/** ANAF scrie cu sedilă (ş, ţ); în română corect e cu virgulă (ș, ț). */
const commaBelow = (s: string) =>
  s.replace(/ş/g, 'ș').replace(/Ş/g, 'Ș').replace(/ţ/g, 'ț').replace(/Ţ/g, 'Ț');

/** ANAF trimite '' în loc de lipsă. */
const dateOrNull = (s: unknown): string | null =>
  typeof s === 'string' && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;

const str = (s: unknown): string => (typeof s === 'string' ? commaBelow(s.trim()) : '');

type Rec = Record<string, unknown>;
const obj = (v: unknown): Rec => (v && typeof v === 'object' ? (v as Rec) : {});

/** Sediul social, din bucățile ANAF: „Șos. Virtuții 148, spatiul E47, Sector 6 Mun. București”. */
function seatAddress(r: Rec): string {
  const a = obj(r.adresa_sediu_social);
  const street = [str(a.sdenumire_Strada), str(a.snumar_Strada)].filter(Boolean).join(' ');
  const place = str(a.sdenumire_Localitate);
  const county = str(a.sdenumire_Judet);
  const countyPart =
    county && !place.toLowerCase().includes(county.toLowerCase().replace(/^municipiul /, ''))
      ? `jud. ${county.charAt(0) + county.slice(1).toLowerCase()}`
      : '';
  const parts = [street, str(a.sdetalii_Adresa), place, countyPart].filter(Boolean);
  return parts.length >= 2 ? parts.join(', ') : str(obj(r.date_generale).adresa);
}

/** O înregistrare din `found[]` a răspunsului v9. */
export function parseAnafRecord(r: Rec, checkedAt: string): AnafInfo {
  const g = obj(r.date_generale);
  const inactiv = obj(r.stare_inactiv);
  return {
    checkedAt,
    found: true,
    name: str(g.denumire),
    address: seatAddress(r),
    regCom: str(g.nrRegCom),
    caen: str(g.cod_CAEN),
    vat: obj(r.inregistrare_scop_Tva).scpTVA === true,
    vatOnCollection: obj(r.inregistrare_RTVAI).statusTvaIncasare === true,
    splitVat: obj(r.inregistrare_SplitTVA).statusSplitTVA === true,
    inactive: inactiv.statusInactivi === true,
    inactiveSince: dateOrNull(inactiv.dataInactivare),
    deregisteredAt: dateOrNull(inactiv.dataRadiere),
    eFactura: g.statusRO_e_Factura === true,
  };
}

/** Răspunsul pentru un CUI pe care ANAF nu-l are. */
export const anafNotFound = (checkedAt: string): AnafInfo => ({
  checkedAt,
  found: false,
  name: '',
  address: '',
  regCom: '',
  caen: '',
  vat: false,
  vatOnCollection: false,
  splitVat: false,
  inactive: false,
  inactiveSince: null,
  deregisteredAt: null,
  eFactura: false,
});

/** Ce trebuie să știe cumpărătorul, cele grave întâi. */
export function anafAlerts(a: AnafInfo | null | undefined): AnafAlert[] {
  if (!a) return [];
  if (!a.found) {
    return [{ level: 'danger', text: tr('CUI-ul nu există la ANAF. Verifică-l pe o factură.') }];
  }
  const out: AnafAlert[] = [];
  if (a.deregisteredAt) {
    out.push({ level: 'danger', text: tr('Firmă radiată din {0}.', mailDate(a.deregisteredAt)) });
  }
  if (a.inactive) {
    out.push({
      level: 'danger',
      text: tr(
        'Inactiv fiscal{0}: facturile lui nu îți dau drept de deducere a TVA și a cheltuielii.',
        a.inactiveSince ? tr(' din {0}', mailDate(a.inactiveSince)) : '',
      ),
    });
  }
  if (!a.vat) out.push({ level: 'info', text: tr('Nu e plătitor de TVA: facturile vin fără TVA.') });
  if (a.vatOnCollection) {
    out.push({
      level: 'info',
      text: tr('Aplică TVA la încasare: deduci TVA-ul abia după ce plătești factura.'),
    });
  }
  return out;
}

/** „conform ANAF, la 10 octombrie 2026” — datele sunt ale ANAF, la data verificării. */
export const anafAsOf = (a: Pick<AnafInfo, 'checkedAt'>): string =>
  tr('conform ANAF, la {0}', mailDate(a.checkedAt.slice(0, 10)));

/** Eticheta scurtă pentru listă: ce e grav, sau null când e în regulă. */
export function anafBadge(a: AnafInfo | null | undefined): string | null {
  if (!a) return null;
  if (!a.found) return tr('CUI inexistent');
  if (a.deregisteredAt) return tr('Radiat');
  if (a.inactive) return tr('Inactiv fiscal');
  return null;
}
